/**
 * Single-row task write helpers. All but `setLongTaskResults` are cache-only (the
 * `set*` half of ADR 056's set/commit split); each takes its writer as a
 * parameter so no caller has to import a hook to write one row.
 */
import type { Task, TaskPatch } from "types/Task";
import type { ResultLink } from "types/TaskRegistry";
import { isTaskStatusValue, shouldStopPolling } from "types/TaskRegistry";
import type { CommitTaskFn, SetTaskFn } from "./useTaskCache";

/**
 * Writes an observed status onto a row. Cache-only, and used by both task kinds:
 * the MCP sweep reports a bare status, while the long-task sweep and poller report
 * a whole status envelope.
 *
 * `statusReason` distinguishes absent from `null`, and the difference matters:
 *
 * - **Absent** leaves the cached reason alone. This is the bare-status caller, which
 *   has nothing to say about it — and must not blank a reason `exhaustTask` recorded
 *   as a halt message, since it does not clear `pollExhausted` either and the card
 *   would then show "stalled" with no explanation.
 * - **`null`** clears it. This is an envelope caller reporting that the tick carried
 *   no reason, and blanking a now-stale one is the intended behavior (ADR 059).
 *
 * `statusRaw` and `cachedAt` need no such distinction: a tick that does not report
 * them leaves the prior value either way.
 */
export function updateTaskStatus(args: {
  jobId: Task["jobId"];
  status: string;
  statusReason?: string | null;
  statusRaw?: string | null;
  cachedAt?: string | null;
  setTask: SetTaskFn;
}): void {
  const { jobId, status, statusReason, statusRaw, cachedAt, setTask } = args;
  setTask(jobId, (prev) => {
    // An unrecognized status string keeps the prior status rather than
    // overwriting a good state with garbage (fail-open). This also absorbs a
    // stale pre-vocabulary-switch value mid-rollout without terminating a live job.
    const next = isTaskStatusValue(status) ? status : prev.status;
    // Refuse to overwrite a terminal status with a non-terminal one. A stale or
    // slow non-terminal tick must not revive a job another tick/tab already
    // committed terminal, and a late `get_task_status` sweep must not race a
    // terminal result (or vice versa).
    if (shouldStopPolling(prev.status) && !shouldStopPolling(next)) {
      return prev;
    }
    return {
      ...prev,
      status: next,
      statusReason:
        statusReason === undefined
          ? prev.statusReason
          : statusReason ?? undefined,
      statusRaw: statusRaw ?? prev.statusRaw,
      statusCachedAt: cachedAt ?? prev.statusCachedAt,
    };
  });
}

/**
 * Marks an MCP task confirmed-active by a live source (a successful
 * non-terminal poll) this session. Cache-only. The badge counts a non-terminal
 * MCP task only once this is set, so a reload-hydrated row whose persisted
 * status is stale-active (MCP-task terminal status is written cache-only, and
 * `GET /chat/tasks` does not refresh it) is excluded until a poll proves it
 * genuinely active.
 */
export function confirmTaskLive({
  jobId,
  setTask,
}: {
  jobId: Task["jobId"];
  setTask: SetTaskFn;
}): void {
  setTask(jobId, (prev) =>
    prev.liveConfirmed ? prev : { ...prev, liveConfirmed: true }
  );
}

/** Sets `pollExhausted: true`. Cache-only (per-session polling budget). */
export function exhaustTask({
  jobId,
  setTask,
  reason,
}: {
  jobId: Task["jobId"];
  setTask: SetTaskFn;
  reason?: string;
}): void {
  setTask(jobId, (prev) => ({
    ...prev,
    pollExhausted: true,
    statusReason: reason ?? prev.statusReason,
  }));
}

/** Clears `pollExhausted`. Cache-only. */
export function resumeTaskPolling({
  jobId,
  setTask,
}: {
  jobId: Task["jobId"];
  setTask: SetTaskFn;
}): void {
  setTask(jobId, (prev) => ({ ...prev, pollExhausted: false }));
}

/**
 * Commits result/archive links to a long-task row (cache + PATCH).
 *
 * Durable rather than cache-only: the links arrive once, from the fetch that
 * produced them, and nothing re-derives them. A cache-only write would lose them
 * on reload, and the card would re-offer "Fetch results" for a harvest that
 * already succeeded.
 *
 * Idempotent, last-write-wins: a re-fetch yields fresh permalinks.
 *
 * An absent `archiveUrl` is omitted from the patch rather than sent as
 * `undefined`: `commitTask` spreads the patch onto the cached row, so including
 * the key would erase an archive URL an earlier fetch already stored. That is
 * reachable whenever a job is fetched twice; the card offers "Fetch results"
 * and then "Retry fetch," and the second envelope carries links without an
 * archive URL.
 */
export function setLongTaskResults(args: {
  jobId: string;
  resultLinks: ResultLink[];
  archiveUrl: string | undefined;
  commitTask: CommitTaskFn;
}): void {
  const { jobId, resultLinks, archiveUrl, commitTask } = args;
  const fields: TaskPatch = { resultLinks };
  if (archiveUrl !== undefined) fields.archiveUrl = archiveUrl;
  commitTask(jobId, fields);
}
